import { useState, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import { datosPersonalesSchema } from './datos-personales.schema'
import { tiposDocumento } from '../../nomina/tiposDocumento.js'
import { ValidationError } from 'yup'
import { OnboardingService } from '../lib/onboarding.service'
import { StepId } from '../lib/utils'
import { parseNombre } from './lib/utils.js'
import encryptRSA from '../../../config/encryptRSA'
import { useUnit } from 'effector-react'
import { $emailVerified, $password, $userId } from '../onboarding.model'

export function useDatosPersonales() {
  // const [searchParams] = useSearchParams()
  const [, setSearchParams] = useSearchParams()
  const email = useUnit($emailVerified)
  const password = useUnit($password)
  const userId = useUnit($userId)

  const [formData, setFormData] = useState({
    tipoDocumento: '',
    documento: '',
    nombreCompleto: '',
    celular: '',
  })

  const [errors, setErrors] = useState({})
  const [status, setStatus] = useState('')

  const validateField = useCallback(async (field, value) => {
    const fieldSchema = datosPersonalesSchema.fields[field]
    const stringValue = typeof value === 'string' ? value.trim() : ''
    if (!stringValue) {
      setErrors((prev) => ({ ...prev, [field]: [] }))
      return
    }
    try {
      await fieldSchema.validate(stringValue)
      setErrors((prev) => ({ ...prev, [field]: [] }))
    } catch (err) {
      if (err instanceof ValidationError) {
        setErrors((prev) => ({ ...prev, [field]: [{ variant: 'error', text: err.message }] }))
      }
    }
  }, [])

  const onFieldChange = useCallback(
    (field, value) => {
      setFormData((prev) => ({ ...prev, [field]: value }))
      validateField(field, value)
    },
    [validateField]
  )

  const onSubmit = async () => {
    try {
      await datosPersonalesSchema.validate(formData, { abortEarly: false })
      const nombreCompleto = formData?.nombreCompleto
      const { firstName, lastName } = parseNombre(nombreCompleto)
      await OnboardingService.executeStep(StepId.DATOS_PERSONALES, {
        email,
        userId,
        credentials: encryptRSA({
          username: email,
          password,
          email,
          firstName,
          lastName,
        }),
        datosPersonales: formData,
      })
      setErrors({})
      setSearchParams({ success: 'true' })
    } catch (err) {
      if (err instanceof ValidationError) {
        const fieldErrors = {}
        err.inner.forEach(({ path, message }) => {
          fieldErrors[path] = [{ variant: 'error', text: message }]
        })
        setErrors(fieldErrors)
        return
      } else if (err?.code === 'CREATE_USER_ERROR') {
        setStatus('CREATE_USER_ERROR')
      }
    }
  }

  const hasErrors = Object.values(errors).some((errorArr) => errorArr && errorArr.length > 0)
  const isFormFilled = ['tipoDocumento', 'documento', 'nombreCompleto'].every(
    (field) => String(formData[field] ?? '').trim() !== ''
  )
  const disabled = hasErrors || !isFormFilled

  return {
    formData,
    errors,
    tiposDocumento,
    onFieldChange,
    onSubmit,
    disabled,
    status,
  }
}
